import type { AtlasManifest } from '@atlas-manifest/types';
import { ObjectHeader } from '../components/ObjectHeader.js';
import { EmptyState } from '../components/EmptyState.js';
import { EntityChip } from '../components/EntityChip.js';
import { Icon } from '../components/Icon.js';
import { EnrichmentFields } from '../components/EnrichmentFields.js';
import { RelationshipsPanel } from '../components/RelationshipsPanel.js';
import { surfaceById } from '../state/selectors.js';
import { useAppStore } from '../state/store.js';
import { navigateToObject, navigate, navigateToObjectKind } from '../state/hashRouter.js';
import { SURFACE_ATTRIBUTE_DOCS } from '../state/attributeGlossary.js';
import { WorkflowTrail } from './WorkflowTrail.js';
import { PresenceContainer } from './PresenceContainer.js';
import { AnnotationsContainer } from './AnnotationsContainer.js';
import { AskAtlasToEditBox } from '../components/AskAtlasToEditBox.js';
import { streamAskAtlas } from '../firebase/atlasApi.js';

export interface SurfaceDetailViewProps {
  manifest: AtlasManifest;
  surfaceId: string;
}

export function SurfaceDetailView({ manifest, surfaceId }: SurfaceDetailViewProps): React.JSX.Element {
  const surface = surfaceById(manifest, surfaceId);
  if (!surface) {
    return <EmptyState icon="help" title={`Surface ${surfaceId} not found.`} />;
  }

  const { entityTrail } = useAppStore();

  return (
    <div className="detail-view">
      <ObjectHeader
        object={surface}
        icon="web"
        iconLabel="Surface"
        badges={[{ label: surface.domain, type: 'domain' as const }]}
        title={surface.id}
        docRef={<>{surface.type} · hub: {surface.hub}</>}
        description={surface.description}
        chips={[
          ...(surface.tierGate ? [{ label: surface.tierGate }] : []),
          ...(surface.packGate ? [{ label: surface.packGate, tone: 'info' as const }] : []),
        ]}
        actions={
          <>
            <button className="btn btn-ghost" onClick={() => navigate({ kind: 'sandbox', seedObject: { kind: 'surface', id: surface.id } })}>
              <Icon name="edit" size="sm" />
              Edit in Sandbox
            </button>
            <button className="btn btn-ghost" onClick={() => navigate({ kind: 'ask', focus: surface.id })}>
              <Icon name="auto_awesome" size="sm" />
              Ask Atlas
            </button>
          </>
        }
      />
      <dl className="sheet-hero-stats" aria-label="stats">
        <div>
          <dt>PAYLOAD FIELDS</dt>
          <dd>{surface.payloadFields.length}</dd>
        </div>
        <div>
          <dt>TIER</dt>
          <dd>{surface.tierGate ?? '—'}</dd>
        </div>
        <div>
          <dt>PACK</dt>
          <dd>{surface.packGate ?? '—'}</dd>
        </div>
        <div>
          <dt>HUB</dt>
          <dd>{surface.hub || '—'}</dd>
        </div>
      </dl>
      <EnrichmentFields
        fields={[
          { key: 'personas', label: 'Personas', value: surface.personas, glossary: SURFACE_ATTRIBUTE_DOCS['personas'] },
          { key: 'conditionalLogic', label: 'Conditional Logic', value: surface.conditionalLogic, glossary: SURFACE_ATTRIBUTE_DOCS['conditionalLogic'] },
          { key: 'a11yNotes', label: 'Accessibility Notes', value: surface.a11yNotes, glossary: SURFACE_ATTRIBUTE_DOCS['a11yNotes'] },
        ]}
      />
      {entityTrail.length >= 2 && (
        <WorkflowTrail trail={entityTrail} onJump={navigateToObject} />
      )}
      <PresenceContainer entityId={surface.id} />
      <AskAtlasToEditBox
        kind="surface"
        selectorId={surface.id}
        currentObject={surface as unknown as Record<string, unknown>}
        manifest={manifest}
        onAsk={(opts) => streamAskAtlas(opts.prompt, opts.summary, opts.knownEntityIds)}
      />
      <section style={{ marginBottom: 'var(--sp-xl)' }}>
        <div className="section-label">Payload · {surface.payloadFields.length} fields</div>
        <table className="field-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Type</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {surface.payloadFields.map((f) => (
              <tr key={f.name}>
                <td>{f.name}</td>
                <td>{f.type}</td>
                <td style={{ color: 'var(--text3)' }}>{f.description ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {surface.entityIds && surface.entityIds.length > 0 && (
        <section style={{ marginBottom: 'var(--sp-xl)' }}>
          <div className="section-label">Backed by · {surface.entityIds.length} entities</div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {surface.entityIds.map((entityId) => (
              <EntityChip key={entityId} entityId={entityId} manifest={manifest} />
            ))}
          </div>
        </section>
      )}
      <RelationshipsPanel manifest={manifest} kind="surface" id={surface.id} onNavigate={navigateToObjectKind} />
      <AnnotationsContainer entityId={surface.id} />
    </div>
  );
}
